import { useSelector } from 'react-redux'
import { useQuery } from 'react-query'
import { useAuth0 } from '../../../contexts/auth0-context'
import callAPI from '../../../API/callSecureAPI'
import endpoints from '../../../API/endpoints'
import { Evidence } from '../../../shared/queries/useGetEvidenceQuery'

export type KeyEvidenceStatement = {
    _id: string
    statement: string
    parentEvidence: Evidence
    figures: string[]
}

export type ValueMessage = {
    _id: string
    name: string
    narrativeId: string
    organizationId: string
    categoryId: string
    createdAt: string
    linkedEvidence: Evidence[]
    linkedActivities: string[]
    evidenceGaps: string[]
    keyEvidenceStatements: KeyEvidenceStatement[]
    audiences: Record<string, boolean>
    internalStakeholders: Record<string, boolean>
    order: number
    localMarkets: string[]
    isCountrySpecific: boolean
    intendedUse?: string[]
}

export type ValueNarrative = {
    _id: string
    name: string
    categoryId: string
    order: number
    subpopulation?: ValueNarrativeSubpopulation
    valueMessages: ValueMessage[]
    locked?: {
        lockedBy: {
            _id: string
            name: string
        }
        lockedAt: string
    }
    archived?: {
        archivedBy: {
            _id: string
            name: string
        }
        archivedAt: string
        archivedNote: string
    }
}

export type ValueNarrativeCategory = {
    _id: string
    name: string
    narratives: ValueNarrative[]
    order: number
    isPseudoCategory?: boolean
}

export type ValueNarrativeSubpopulation = string
export type ValueNarrativeInternalStakeholder = string

// normally set to 1000 * 60, updating for error hotfix AT-382
const REFRESH_RATE_MS = 3000 * 60

export const getValueNarrativesQueryKey = (strategyId?: string) => ['valueNarratives', strategyId]

export const useGetValueNarrativesQuery = () => {
    const { getTokenSilently, isAuthenticated, isLoading } = useAuth0()

    // @ts-expect-error store is not typed properly
    const strategyId = useSelector((state) => state.strategy?._id)

    const getValueNarratives = async () => {
        try {
            const token = await getTokenSilently?.({ audience: 'https://atlas.aesara.com' })

            const { url, method } = endpoints.getValueNarratives

            const response = await callAPI(url + strategyId, method, token)

            return response.data
        } catch (e) {
            console.log(e)
        }
    }

    return useQuery<ValueNarrativeCategory[]>(getValueNarrativesQueryKey(strategyId), getValueNarratives, {
        enabled: !!getTokenSilently && !!isAuthenticated && !isLoading,
        cacheTime: REFRESH_RATE_MS,
        staleTime: REFRESH_RATE_MS,
        refetchOnWindowFocus: false,
        retry: 3,
    })
}
